// Client-side bookkeeping of purchases tracked on the current page.
//
// Targeting rules on eCommerce attributes (totalRevenue, ordersCount, SKUs,
// coupons ...) must react to a purchase right away. The backend aggregates
// purchases asynchronously, so the attributes reloaded after `api/purchase`
// may not contain the order yet. The purchase is therefore applied to the
// attributes locally and reconciled with the server answer.

const NUMERIC_USER_KEYS = ['ordersCount', 'totalPurchases', 'totalRevenue', 'totalAov'];
const TEXT_USER_KEYS = ['customerType', 'country', 'language'];
const LIST_ORDER_KEYS = [
    'orderItemSkus',
    'itemBrands',
    'couponCodes',
    'category',
    'category2',
    'category3',
    'category4',
    'category5',
];

function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function toNumber(value) {
    const number = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(number) ? number : 0;
}

function toList(value) {
    if (Array.isArray(value)) {
        return value.filter((v) => v !== null && v !== undefined && v !== '').map(String);
    }
    if (value === null || value === undefined || value === '') return [];
    return [String(value)];
}

function union(a, b) {
    return Array.from(new Set([...toList(a), ...toList(b)]));
}

// Missing numbers count as 0, missing lists as empty. Text attributes and
// unknown keys are kept as they are.
export function normalizeAttributes(attributes) {
    const source = isPlainObject(attributes) ? attributes : {};
    const user = isPlainObject(source.userAttributes) ? source.userAttributes : {};
    const order = isPlainObject(source.orderAttributes) ? source.orderAttributes : {};

    const result = {
        ...source,
        userAttributes: { ...user },
        orderAttributes: { ...order },
        customUserAttributes: isPlainObject(source.customUserAttributes) ? source.customUserAttributes : {},
    };

    NUMERIC_USER_KEYS.forEach((key) => {
        result.userAttributes[key] = toNumber(user[key]);
    });
    LIST_ORDER_KEYS.forEach((key) => {
        result.orderAttributes[key] = toList(order[key]);
    });

    return result;
}

function itemValue(item, ...keys) {
    for (const key of keys) {
        if (item[key] !== undefined && item[key] !== null && item[key] !== '') return item[key];
    }
    return null;
}

// Returns a new attributes object with the purchase added on top of
// `attributes` (the attributes as they were before the purchase).
export function applyPurchaseToAttributes(attributes, purchase = {}) {
    const result = normalizeAttributes(attributes);
    const user = result.userAttributes;
    const order = result.orderAttributes;
    const amount = toNumber(purchase.value ?? purchase.amount);

    user.ordersCount += 1;
    user.totalPurchases += 1;
    user.totalRevenue += amount;
    user.totalAov = user.ordersCount > 0 ? user.totalRevenue / user.ordersCount : 0;

    order.couponCodes = union(order.couponCodes, purchase.coupon);

    const items = Array.isArray(purchase.items) ? purchase.items : [];
    items.forEach((item) => {
        if (!isPlainObject(item)) return;
        order.orderItemSkus = union(order.orderItemSkus, itemValue(item, 'sku', 'item_id', 'id'));
        order.itemBrands = union(order.itemBrands, itemValue(item, 'brand', 'item_brand'));
        order.category = union(order.category, itemValue(item, 'category', 'item_category'));
        [2, 3, 4, 5].forEach((n) => {
            order[`category${n}`] = union(order[`category${n}`], itemValue(item, `category${n}`, `item_category${n}`, `item_category_${n}`));
        });
    });

    return result;
}

// The server answer may or may not already include the purchases tracked
// locally, so totals take the larger value and lists the union. Text
// attributes prefer the server, falling back to the local copy.
export function mergeAttributes(server, local) {
    const s = normalizeAttributes(server);
    const l = normalizeAttributes(local);

    const result = {
        ...l,
        ...s,
        userAttributes: { ...l.userAttributes, ...s.userAttributes },
        orderAttributes: { ...l.orderAttributes, ...s.orderAttributes },
        customUserAttributes: Object.keys(s.customUserAttributes).length > 0 ? s.customUserAttributes : l.customUserAttributes,
    };

    NUMERIC_USER_KEYS.forEach((key) => {
        result.userAttributes[key] = Math.max(s.userAttributes[key], l.userAttributes[key]);
    });
    TEXT_USER_KEYS.forEach((key) => {
        result.userAttributes[key] = s.userAttributes[key] ?? l.userAttributes[key] ?? null;
    });
    LIST_ORDER_KEYS.forEach((key) => {
        result.orderAttributes[key] = union(s.orderAttributes[key], l.orderAttributes[key]);
    });

    return result;
}
